import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormHelperText, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useAtomValue, useSetAtom } from 'jotai';
import { Company_Set_Input, Marketing_Support_Enum, Return_Policy_Enum } from '@kheops/graphql';
import { useUpdateCompanyMutation } from '../../mutations/__generated__/updateCompany.generated';
import { useUpsertCompanyMarketingSupportsMutation } from '../../mutations/__generated__/upsertCompanyMarketingSupport.generated';
import { UserRoleDocument } from '../../queries/__generated__/userRole.generated';
import { currentContextAtom } from '../../state';
import { commonSnackbarPropsAtom } from '../../common/state/state';
import { ServiceConfiguration } from './state';
import AvailabilityChip from './AvailabilityChip';
import ReturnPolicySelect from './ReturnPolicySelect';
import MarketingSupportsSelect from './MarketingSupportsSelect';

export interface ServiceConfigurationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  configuration: ServiceConfiguration;
  values?: Return_Policy_Enum | Marketing_Support_Enum[];
}

interface ServiceConfigurationForm {
  enabled: boolean | null;
  details?: string | null;
  values?: unknown;
}

export default function ServiceConfigurationDialog({ isOpen, onClose, configuration }: ServiceConfigurationDialogProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'settings', 'validation']);
  const { icon, wordingNamespace, service, serviceDetails, getServiceValues } = configuration;
  const { companyId, companyServices } = useAtomValue(currentContextAtom);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const [updateCompany, { loading: updateCompanyLoading, called: updateCompanyCalled, error: updateCompanyError }] = useUpdateCompanyMutation({ refetchQueries: [UserRoleDocument], awaitRefetchQueries: true });
  const [upsertCompanyMarketingSupports, { loading: upsertCompanyMarketingSupportsLoading, error: upsertCompanyMarketingSupportsError }] = useUpsertCompanyMarketingSupportsMutation();
  const { control, formState, watch, handleSubmit } = useForm<ServiceConfigurationForm>({
    defaultValues: {
      enabled: companyServices[service],
      details: companyServices[serviceDetails],
      values: getServiceValues ? getServiceValues(companyServices): undefined,
    },
  });
  const enabled = watch('enabled');

  const onSubmit = (submitValues: ServiceConfigurationForm): void => {
    const companyUpdateData: Company_Set_Input = {
      [configuration.service]: submitValues.enabled,
      [configuration.serviceDetails]: submitValues.enabled ? submitValues.details : null,
      updated_at: new Date().toISOString(),
    };

    switch (service) {
      case 'provide_marketing':
        const marketingSupports = submitValues.enabled
          ? (submitValues.values as Marketing_Support_Enum[]).map((support) => ({
            company_id: companyId,
            support,
          }))
          : [];

        upsertCompanyMarketingSupports({
          variables: {
            companyId,
            marketing_supports: marketingSupports,
          }
        })
        break;
      case 'provide_return_policy':
        companyUpdateData.return_policy = submitValues.enabled ? submitValues.values as Return_Policy_Enum: null;
    }

    updateCompany({
      variables: {
        companyId,
        companyUpdateData,
      },
    });
  };

  useEffect((): void => {
    if (updateCompanyLoading || upsertCompanyMarketingSupportsLoading) {
      return;
    }

    if (updateCompanyCalled) {
      if (!updateCompanyError && !upsertCompanyMarketingSupportsError) {
        setCommonSnackbarProps({
          label: t('settings:your_profile_has_been_updated'),
          snackbarProps: {
            open: true,
          },
        });
        onClose();
      } else {
        setCommonSnackbarProps({
          label: t('error:an_error_occured'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            severity: 'error',
          },
        });
      }
    }
  }, [updateCompanyLoading, updateCompanyCalled, updateCompanyError, upsertCompanyMarketingSupportsLoading, upsertCompanyMarketingSupportsError]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          minWidth: {
            xs: 'calc(100vw - 64px)',
            sm: 640,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Box sx={{ p: 0.5, borderRadius: 1, backgroundColor: 'surfaceContainerLow.main', height: 28 }}>
            <img src={icon} height="28" width="28" alt="" />
          </Box>
          <Typography>{t(`settings:${wordingNamespace}.title`)}</Typography>
        </Box>
        <Typography variant="bodyMedium">
          {t(`settings:${wordingNamespace}.description`)}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="titleMedium">
            {t('settings:do_you_offer_this_service')}*
          </Typography>
          <Controller
            control={control}
            name="enabled"
            render={({ field }) => (
              <RadioGroup
                {...field}
                onChange={(e) => {
                  field.onChange(e.target.value === 'true');
                }}
                row
              >
                <FormControlLabel
                  labelPlacement="end"
                  value="true"
                  control={<Radio />}
                  label={t('settings:yes')}
                />
                <FormControlLabel
                  labelPlacement="end"
                  value="false"
                  control={<Radio />}
                  label={t('settings:no')}
                />
              </RadioGroup>
            )}
          />
          <Box>
            <AvailabilityChip value={enabled} />
          </Box>
        </Box>
        {enabled && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'outlineVariant.main',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {configuration.getServiceValues && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="labelMedium">
                  {t(`settings:${wordingNamespace}.values.title`)}*
                </Typography>
                <Controller
                  control={control}
                  name="values"
                  rules={{ required: t('validation:this_field_is_required') as string }}
                  render={({ field, fieldState }) => (
                    <>
                      {service === 'provide_marketing' && (
                        <MarketingSupportsSelect values={field.value as Marketing_Support_Enum[]} onChange={field.onChange} />
                      )}
                      {service === 'provide_return_policy' && (
                        <ReturnPolicySelect value={field.value as Return_Policy_Enum} onChange={field.onChange} />
                      )}
                      {fieldState.error
                        && <FormHelperText error>{fieldState.error?.message}</FormHelperText>}
                    </>
                  )}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="labelMedium">
                {t('common:details')}
              </Typography>
              <Controller
                control={control}
                name="details"
                render={({ field }) => (
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder={t(`settings:${wordingNamespace}.placeholder`)}
                    {...field}
                  />
                )}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        <Button variant="contained" disabled={!formState.isDirty} onClick={handleSubmit(onSubmit)}>
          {typeof enabled === 'boolean' && !enabled ? t('settings:disable') : t('settings:enable')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
